import AddIcon from "@mui/icons-material/Add";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ragChatbotStyles from "../RagChatbot.styles";

export interface ChatSession {
  id: string;
  name: string;
  args: {
    filename?: string;
    category?: string;
  };
}

interface ChatHistoryProps {
  chatSessions: ChatSession[];
  currentChatId: string | null;
  onSelectChat: (session: ChatSession) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  isLoading: boolean;
}

const ChatHistory = ({
  chatSessions,
  currentChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  isLoading,
}: ChatHistoryProps) => {
  const classes = ragChatbotStyles;
  const { t } = useTranslation();

  const handleDeleteClick = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    onDeleteChat(chatId);
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.header}>
        <Typography sx={classes.headerTitle}>
          <ChatIcon fontSize="small" />
          {t("chatHistory")}
        </Typography>
        <Tooltip title={t("newChat")}>
          <IconButton sx={classes.newChatButton} onClick={onNewChat}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={classes.chatList}>
        {isLoading ? (
          <List sx={{ p: 1 }}>
            {[...Array(5)].map((_, index) => (
              <ListItem key={index} sx={classes.chatItem} disablePadding>
                <Box sx={{ width: "100%" }}>
                  <Skeleton
                    variant="text"
                    width={"100%"}
                    height={45}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      mb: 0.5,
                    }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        ) : chatSessions.length === 0 ? (
          <Box sx={classes.emptyState}>
            <ChatIcon sx={{ fontSize: "48px", color: "#ffffff30" }} />
            <Typography sx={classes.emptyStateText}>
              {t("noChat")}
              <br />
              {t("startChat")}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 1 }}>
            {chatSessions.map((session) => (
              <ListItem
                key={session.id}
                sx={[
                  classes.chatItem,
                  currentChatId === session.id && classes.activeChatItem,
                ]}
                disablePadding
              >
                <ListItemButton
                  sx={classes.chatItemButton}
                  onClick={() => onSelectChat(session)}
                >
                  <Typography sx={classes.chatSessionText}>
                    {session.name}
                  </Typography>
                  <IconButton
                    className="delete-button"
                    sx={classes.deleteButton}
                    onClick={(e) => handleDeleteClick(e, session.id)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ChatHistory;
